import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { isTenantSuspendedError } from "./lib/tenant-status";
import { shouldRetryQuery, retryDelay } from "./lib/query-retry";
import { AuthProvider } from "./components/AuthProvider";
import { RequireAuth } from "./components/RequireAuth";
import { LoginView, JoinView } from "./views/LoginView";
import { DeskView } from "./views/DeskView";
import { DeskQueueView } from "./views/DeskQueueView";
import { DeskBoardView } from "./views/DeskBoardView";
import { CalendarView } from "./views/CalendarView";
import { SocialCalendarView } from "./views/SocialCalendarView";
import { PieceView } from "./views/PieceView";
import { Shell } from "./components/Shell";
import { SettingsShell } from "./components/SettingsShell";
import { RequireTenant } from "./components/RequireTenant";
import { OnboardingView } from "./views/OnboardingView";
import { WorkspacesView } from "./views/WorkspacesView";
import { TeamView } from "./views/TeamView";
import { PublicationsView } from "./views/PublicationsView";
import { VoiceProfileEditor, VoiceProfileCreateView } from "./views/VoiceProfileEditor";
import { VoiceProfilesIndexView } from "./views/VoiceProfilesIndexView";
import { BrandIdentityEditor } from "./views/BrandIdentityEditor";
import { BrandIdentityIndexView } from "./views/BrandIdentityIndexView";
import { PublishTargetsView } from "./views/PublishTargetsView";
import { AmplifiersView } from "./views/AmplifiersView";
import { SocialAccountsView } from "./views/SocialAccountsView";
import { ApiKeysView } from "./views/ApiKeysView";
import { LibraryView } from "./views/LibraryView";
import { AccountSettingsView } from "./views/AccountSettingsView";
import { AnalyticsView } from "./views/AnalyticsView";
import { NewsletterView } from "./views/NewsletterView";
import { AdminAnalyticsView } from "./views/AdminAnalyticsView";
import { AdminTenantsView } from "./views/AdminTenantsView";
import { AdminAuditView } from "./views/AdminAuditView";
import { Analytics } from "./components/Analytics";

// FEAT-106 / S-425 — any call refused with `tenant_suspended` re-reads
// /tenants/me, so a tab left open switches to the suspended notice
// (RequireTenant) instead of showing errors one screen at a time.
function onTenantSuspended(err: unknown) {
  if (isTenantSuspendedError(err)) void queryClient.invalidateQueries({ queryKey: ["tenants", "me"] });
}

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onTenantSuspended }),
  mutationCache: new MutationCache({ onError: onTenantSuspended }),
  defaultOptions: {
    queries: {
      // FEAT-044 — tolerate backend cold starts: retry network/502/503/504 with
      // backoff up to a cap instead of erroring after 2 tries. 401/403 never retry.
      retry: shouldRetryQuery,
      retryDelay,
      staleTime: 30_000,
    },
  },
});

/**
 * S-086 — bridge component for legacy /article/:id URLs. `<Navigate>`
 * can't read route params inline, so we hop through a one-line
 * functional component to extract `id` and forward to /piece/:id.
 */
function LegacyArticleRedirect() {
  const { id } = useParams();
  return <Navigate to={`/piece/${id ?? ""}`} replace />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Analytics />
          <Routes>
            {/* Clerk's <SignIn routing="path" /> mounts sub-routes
                (e.g. /login/factor-one, /login/sso-callback) so the
                login route must match deeper paths too. */}
            <Route path="/login/*" element={<LoginView />} />
            {/* BUG-077 — request access, on this domain rather than Clerk's
                hosted portal. The instance is in waitlist mode, so this is
                where "sign up" means "ask to be let in". */}
            <Route path="/join" element={<JoinView />} />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <RequireTenant>
                    <Shell />
                  </RequireTenant>
                </RequireAuth>
              }
            >
              {/* FEAT-087 — the Desk is the home screen now: one
                  prioritised queue of what is waiting on a person,
                  replacing a board-first landing. Its views are routes so
                  they can be linked to and the sidebar has something to
                  match on; Board still lives at /kanban until S-366 folds
                  it in, and Week/4 weeks/Month arrive with Phase 4. */}
              <Route index element={<Navigate to="/desk" replace />} />
              <Route path="desk" element={<DeskView />}>
                <Route index element={<DeskQueueView />} />
                <Route path="board" element={<DeskBoardView />} />
              </Route>
              {/* S-366 — the board moved into the Desk shell. The old route
                  redirects rather than 404s: it was the app's home for a
                  long time, so it is in muscle memory and in links. */}
              <Route path="kanban" element={<Navigate to="/desk/board" replace />} />
              <Route path="calendar" element={<CalendarView />} />
              <Route path="social" element={<SocialCalendarView />} />
              <Route path="analytics" element={<AnalyticsView />} />
              <Route path="newsletter" element={<NewsletterView />} />
              <Route path="admin/analytics" element={<AdminAnalyticsView />} />
              <Route path="admin/tenants" element={<AdminTenantsView />} />
              <Route path="admin/audit-log" element={<AdminAuditView />} />
              <Route path="piece/:id" element={<PieceView />} />
              {/* S-086 — legacy /article/:id bookmarks redirect to the
                  new /piece/:id route so external links still resolve. */}
              <Route path="article/:id" element={<LegacyArticleRedirect />} />
              {/* Admin routes (FEAT-006 S-058). All real implementations
                  shipped in Phases 3-9 (S-059 → S-073). */}
              <Route path="onboarding" element={<OnboardingView />} />
              {/* FEAT-048 Phase 1: nested under SettingsShell so every
                  settings section renders inside one persistent left-rail
                  + content-pane frame, matching the design handoff at
                  design_handoff_settings_unified/. Voice/Brand stay as
                  standalone routes until Phase 2 moves them in-pane. */}
              <Route path="settings" element={<SettingsShell />}>
                <Route index element={<Navigate to="workspaces" replace />} />
                <Route path="workspaces" element={<WorkspacesView />} />
                <Route path="team" element={<TeamView />} />
                <Route path="publications" element={<PublicationsView />} />
                <Route path="publish-targets" element={<PublishTargetsView />} />
                <Route path="voice" element={<VoiceProfilesIndexView />} />
                <Route path="voice/new" element={<VoiceProfileCreateView />} />
                <Route
                  path="voice/:voiceProfileId"
                  element={<VoiceProfileEditor />}
                />
                <Route path="brand" element={<BrandIdentityIndexView />} />
                <Route
                  path="brand/:publicationId"
                  element={<BrandIdentityEditor />}
                />
                <Route path="library" element={<LibraryView />} />
                <Route path="amplifiers" element={<AmplifiersView />} />
                <Route path="social-accounts" element={<SocialAccountsView />} />
                <Route path="api-keys" element={<ApiKeysView />} />
                <Route path="account" element={<AccountSettingsView />} />
              </Route>
              <Route path="*" element={<Navigate to="/desk" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
